import { Controller } from "@hotwired/stimulus";

/**
 * Sidebar accordion (Section I4).
 *
 * Each `[data-accordion-target="trigger"]` is a real <button aria-expanded
 * aria-controls> that shows/hides its controlled <ul> panel. Real buttons give
 * Enter/Space toggling for free. The server renders every panel EXPANDED so nav
 * still works with JS disabled (progressive enhancement); on connect we collapse
 * to the default/persisted state.
 *
 * Rules:
 *  - Default collapsed, EXCEPT the section/group containing the active link.
 *  - Multiple panels may be open at once (not an exclusive accordion).
 *  - Open/closed state is persisted to localStorage keyed by the label text.
 *  - Deep-linking into a collapsed group auto-expands its ancestors.
 */
export default class extends Controller {
  static targets = ["trigger"];

  connect() {
    this._storageKey = "docs-sidebar-accordion";
    this._boundHashChange = this._onHashChange.bind(this);
    window.addEventListener("hashchange", this._boundHashChange);

    const persisted = this._readState();
    const hash = this._currentHash();
    const activeLink = this._linkForHash(hash) || this._firstLink();

    this.triggerTargets.forEach((trigger) => {
      const panel = this._panelFor(trigger);
      if (!panel) return;

      const label = this._labelFor(trigger);
      const containsActive = !!activeLink && panel.contains(activeLink);
      const saved = persisted[label];

      let open;
      if (hash && containsActive) {
        // Deep-link into this group — always reveal it, overriding a saved-closed.
        open = true;
      } else if (typeof saved === "boolean") {
        open = saved;
      } else {
        open = containsActive;
      }

      this._apply(trigger, panel, open);
    });
  }

  disconnect() {
    window.removeEventListener("hashchange", this._boundHashChange);
  }

  // ── Actions ────────────────────────────────────────────────────────────────

  toggle(event) {
    const trigger = event.currentTarget;
    const panel = this._panelFor(trigger);
    if (!panel) return;

    const open = trigger.getAttribute("aria-expanded") !== "true";
    this._apply(trigger, panel, open);
    this._saveState(this._labelFor(trigger), open);
  }

  // ── Internals ────────────────────────────────────────────────────────────────

  _onHashChange() {
    const activeLink = this._linkForHash(this._currentHash());
    if (!activeLink) return;

    // Walk up and expand every accordion panel that contains the linked item so
    // a deep link into a collapsed group reveals it in the nav.
    let el = activeLink.parentElement;
    while (el && el !== this.element) {
      if (
        el.matches('[data-accordion-target="panel"]') &&
        el.hidden
      ) {
        const trigger = this._triggerFor(el);
        if (trigger) {
          this._apply(trigger, el, true);
          this._saveState(this._labelFor(trigger), true);
        }
      }
      el = el.parentElement;
    }
  }

  _apply(trigger, panel, open) {
    trigger.setAttribute("aria-expanded", open ? "true" : "false");
    panel.hidden = !open;
  }

  _panelFor(trigger) {
    const id = trigger.getAttribute("aria-controls");
    return id ? document.getElementById(id) : null;
  }

  _triggerFor(panel) {
    return this.triggerTargets.find(
      (t) => t.getAttribute("aria-controls") === panel.id
    );
  }

  _labelFor(trigger) {
    const span = trigger.querySelector(".docs-sidebar-label-text");
    return (span ? span.textContent : trigger.textContent).trim();
  }

  _currentHash() {
    return window.location.hash.slice(1).split("?")[0].replace(/\/$/, "").trim();
  }

  _linkHash(link) {
    const href = link.getAttribute("href") || "";
    const i = href.indexOf("#");
    if (i === -1) return "";
    return href.slice(i + 1).split("?")[0].replace(/\/$/, "").trim();
  }

  _linkForHash(hash) {
    if (!hash) return null;
    const links = this.element.querySelectorAll("a.docs-sidebar-link");
    for (const link of links) {
      if (this._linkHash(link) === hash) return link;
    }
    return null;
  }

  _firstLink() {
    return this.element.querySelector("a.docs-sidebar-link");
  }

  _readState() {
    try {
      const raw = window.localStorage.getItem(this._storageKey);
      const parsed = raw ? JSON.parse(raw) : {};
      return parsed && typeof parsed === "object" ? parsed : {};
    } catch (e) {
      return {};
    }
  }

  _saveState(label, open) {
    if (!label) return;
    try {
      const state = this._readState();
      state[label] = open;
      window.localStorage.setItem(this._storageKey, JSON.stringify(state));
    } catch (e) {
      /* localStorage unavailable (private mode / quota) — non-fatal. */
    }
  }
}
